/** @jsxImportSource @emotion/react */

import { useEffect, useRef, useState } from "react";
import {
  css,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import Table from "components/common/Table";
import BaseInput from "components/common/Input/Base";
import BaseButton from "components/common/Button/Base";
import AlertDialog, { AlertData } from "components/common/Dialog/Alert";
import { SELECT_BUSINESS_COLS } from "./column.data";
import { PaymentNoticeMatchResult } from "models/paymentNotice.model";
import { matchPaymentNotice } from "pages/remotes";
import { PALETTE } from "styles/base/variables";

export interface MatchCandidateBusiness {
  id: string;
  name: string;
  regNumber: string;
  incharge?: string;
}

interface SelectBusinessDialogProps {
  isOpen: boolean;
  organizationId: string | null;
  paymentNoticeId: string | null;
  // 현재 매칭된 사업자 - 후보 목록에서 제외한다.
  excludeBusinessId?: string | null;
  businessList: MatchCandidateBusiness[];
  onClose: () => void;
  onSuccess: (businessId: string) => void;
}

function SelectBusinessDialog({
  isOpen,
  organizationId,
  paymentNoticeId,
  excludeBusinessId,
  businessList,
  onClose,
  onSuccess,
}: SelectBusinessDialogProps) {
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [filteredList, setFilteredList] = useState<MatchCandidateBusiness[]>(
    []
  );
  const [selectedBusiness, setSelectedBusiness] =
    useState<MatchCandidateBusiness | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertDialogData, setAlertDialogData] = useState<AlertData>({
    message: "",
  });

  useEffect(() => {
    if (!isOpen) return;
    setFilteredList(
      businessList.filter((business) => business.id !== excludeBusinessId)
    );
    setSelectedBusiness(null);
    if (searchInputRef.current) searchInputRef.current.value = "";
  }, [isOpen, businessList, excludeBusinessId]);

  const closeDialog = () => {
    if (isSubmitting) return;
    onClose();
  };

  const searchHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const keyword = e.target.value;
    const base = businessList.filter(
      (business) => business.id !== excludeBusinessId
    );

    if (!keyword) {
      setFilteredList(base);
      return;
    }

    setFilteredList(
      base.filter(
        (business) =>
          business.name?.includes(keyword) ||
          business.regNumber?.includes(keyword)
      )
    );
  };

  const confirmMatch = async () => {
    if (
      !organizationId ||
      !paymentNoticeId ||
      !selectedBusiness ||
      isSubmitting
    )
      return;

    setIsSubmitting(true);
    try {
      const res = await matchPaymentNotice(organizationId, [
        { paymentNoticeId, businessId: selectedBusiness.id },
      ]);
      const results: PaymentNoticeMatchResult[] = res.data || [];
      const result = results[0];

      if (result && !result.success) {
        setIsSubmitting(false);
        setAlertDialogData({
          message: result.errorMessage || "매칭 변경 중 오류가 발생했습니다.",
          type: "error",
        });
        setIsAlertOpen(true);
        return;
      }

      setIsSubmitting(false);
      onSuccess(selectedBusiness.id);
    } catch (err: any) {
      setIsSubmitting(false);
      setAlertDialogData({
        message:
          err.response?.data?.message || "매칭 변경 중 오류가 발생했습니다.",
        type: "error",
      });
      setIsAlertOpen(true);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>매칭 사업자 변경</DialogTitle>
        <DialogContent>
          <Grid display="flex" flexDirection="column" gap={2}>
            <BaseInput
              ref={searchInputRef}
              placeholder="사업자 검색 (사업자명, 등록번호)"
              css={css`
                width: 100%;
              `}
              onInput={searchHandler}
            />
            <Table
              columns={SELECT_BUSINESS_COLS}
              dataList={filteredList}
              highlightRowId={selectedBusiness?.id}
              minWidth={480}
              minHeight="calc(100vh - 420px)"
              clickRow={setSelectedBusiness}
            />
            <Grid
              height={20}
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              <Grid display="flex" alignItems="center">
                {selectedBusiness ? (
                  <>
                    <strong>
                      {selectedBusiness.name || "(사업자명 없음)"}
                    </strong>
                    <IconButton
                      sx={{ ml: -0.75 }}
                      onClick={() => setSelectedBusiness(null)}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  "매칭할 사업자를 선택해주세요"
                )}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid
            width="100%"
            display="flex"
            marginX={2}
            paddingTop={2}
            paddingBottom={1}
            justifyContent="flex-end"
            gap={1}
            borderTop={"1px solid " + PALETTE.border}
          >
            <BaseButton
              variant="outlined"
              color={PALETTE.primary}
              disabled={isSubmitting}
              onClick={closeDialog}
              style={{ marginRight: 8 }}
            >
              닫기
            </BaseButton>
            <BaseButton
              variant="contained"
              color={PALETTE.primary}
              disabled={isSubmitting || !selectedBusiness}
              onClick={confirmMatch}
            >
              {isSubmitting ? "처리 중" : "확인"}
            </BaseButton>
          </Grid>
        </DialogActions>
      </Dialog>
      <AlertDialog
        isOpen={isAlertOpen}
        setIsOpen={setIsAlertOpen}
        alertData={alertDialogData}
      />
    </>
  );
}

export default SelectBusinessDialog;
